import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, FileText, AlertTriangle, CheckCircle2, RefreshCw, Send, ExternalLink } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useToast } from '../../../src/contexts/ToastContext';
import { DIAS_ANTES_EMITIR } from '../../../supabase/functions/_shared/parcelasParaEmitir';
import {
    ACAO_ETAPA, DIAS_LEMBRAR, SELECT_ACOMPANHAMENTO, montarAcompanhamento,
    type GrupoAcompanhamento, type LinhaAcompanhamento,
} from '../../../supabase/functions/_shared/etapaBoletoParcela.ts';
import EtapaBoletoBadge from './EtapaBoletoBadge';
import { useFaturaDaParcela } from './useFaturaDaParcela';

// Aba "Emitir e enviar" de Parcelamentos. Nasceu como "Para emitir" (#6205): as
// parcelas que vencem em até 10 dias sem boleto. Com o #6540 ganhou o resto do
// caminho de cada mês: o boleto emitido que falta enviar, o enviado que vence em
// breve (para lembrar o associado) e o que venceu sem pagamento.
//
// O sistema continua sem emitir sozinho (decisão de 09/10/2026). Os botões abrem
// as MESMAS telas das faturas mensais — emitir, enviar pelo WhatsApp/e-mail, ver a
// cobrança enviada —, então nada aqui manda mensagem por um caminho novo.

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso: string) => iso.split('-').reverse().join('/');
const hojeBrasilia = () => new Date(Date.now() - 3 * 3600_000).toISOString().slice(0, 10);

const GRUPOS: Array<{ id: GrupoAcompanhamento; rotulo: string; explica: string; vazio: string }> = [
    {
        id: 'emitir',
        rotulo: 'Para emitir',
        explica: `Vencem em até ${DIAS_ANTES_EMITIR} dias, ou já venceram, e ainda não têm boleto.`,
        vazio: `Todas as parcelas dos próximos ${DIAS_ANTES_EMITIR} dias já têm boleto.`,
    },
    {
        id: 'enviar',
        rotulo: 'Para enviar',
        explica: 'Boleto emitido que ainda não foi enviado ao associado. O envio é o mesmo das faturas mensais: WhatsApp e e-mail.',
        vazio: 'Nenhum boleto de parcela esperando envio.',
    },
    {
        id: 'lembrar',
        rotulo: 'Vencem em breve',
        explica: `Boleto já enviado que vence em até ${DIAS_LEMBRAR} dias: bom momento para lembrar o associado.`,
        vazio: `Nenhuma parcela enviada vence nos próximos ${DIAS_LEMBRAR} dias.`,
    },
    {
        id: 'vencidas',
        rotulo: 'Vencidas',
        explica: 'Boleto enviado e vencido sem pagamento.',
        vazio: 'Nenhuma parcela vencida sem pagamento.',
    },
];

const VAZIO: Record<GrupoAcompanhamento, LinhaAcompanhamento[]> = { emitir: [], enviar: [], lembrar: [], vencidas: [] };

const botaoPrimario = 'inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-primary/90 active:scale-[0.97] disabled:opacity-60';
const botaoSecundario = 'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 transition-[background-color,transform] duration-150 ease-out hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.97] disabled:opacity-60';

const corContagem = (id: GrupoAcompanhamento, n: number) => {
    if (n === 0) return 'bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-300';
    if (id === 'vencidas') return 'bg-red-500 text-white';
    if (id === 'lembrar') return 'bg-sky-500 text-white';
    return 'bg-amber-500 text-white';
};

const ParcelasParaEmitir: React.FC<{ onContagem?: (n: number) => void }> = ({ onContagem }) => {
    const { addToast } = useToast();
    const navigate = useNavigate();
    const [grupos, setGrupos] = useState(VAZIO);
    const [grupo, setGrupo] = useState<GrupoAcompanhamento>('emitir');
    const [carregando, setCarregando] = useState(true);
    // Ref: o pai passa a função inline; nas dependências ela recarregaria em loop.
    const onContagemRef = useRef(onContagem);
    onContagemRef.current = onContagem;

    const carregar = useCallback(async () => {
        setCarregando(true);
        const { data, error } = await supabase
            .from('parcelamento_parcelas')
            .select(SELECT_ACOMPANHAMENTO)
            .not('fatura_id', 'is', null)
            .in('acordo.status', ['ativo', 'em_atraso'])
            .limit(2000);
        if (error) {
            addToast('Não foi possível carregar as parcelas: ' + error.message, 'error');
        } else {
            const g = montarAcompanhamento((data || []) as any[], hojeBrasilia());
            setGrupos(g);
            // O número da aba é o que pede ação da equipe: emitir e enviar.
            onContagemRef.current?.(g.emitir.length + g.enviar.length);
        }
        setCarregando(false);
    }, [addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    const { abrir, abrindo, telas } = useFaturaDaParcela(carregar);

    const abrirEmFaturas = (l: LinhaAcompanhamento) => {
        const mes = l.mes_referencia ? `&mes=${encodeURIComponent(l.mes_referencia)}` : '';
        navigate(`/financeiro/faturas?busca=${encodeURIComponent(l.uc)}${mes}`);
    };

    const atual = GRUPOS.find(g => g.id === grupo)!;
    const linhas = grupos[grupo];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div role="tablist" aria-label="Situação das parcelas" className="flex flex-wrap items-center gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
                    {GRUPOS.map(g => {
                        const n = grupos[g.id].length;
                        const ativo = g.id === grupo;
                        return (
                            <button
                                key={g.id}
                                type="button"
                                role="tab"
                                aria-selected={ativo}
                                onClick={() => setGrupo(g.id)}
                                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${ativo
                                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                            >
                                {g.rotulo}
                                {!carregando ? (
                                    <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${corContagem(g.id, n)}`}>{n}</span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
                <button type="button" onClick={carregar} className={botaoSecundario}>
                    <RefreshCw size={13} /> Atualizar
                </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
                {atual.explica}{grupo === 'emitir' ? ' O boleto emitido aparece na hora no link do acordo do associado.' : null}
            </p>

            {carregando ? (
                <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-primary" /></div>
            ) : linhas.length === 0 ? (
                <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
                    <CheckCircle2 size={28} className="mx-auto text-emerald-500" />
                    <p className="mt-2 font-semibold text-gray-800 dark:text-gray-100">Nada aqui agora</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{atual.vazio}</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                            <tr>
                                <th className="px-4 py-3">Cliente</th>
                                <th className="px-4 py-3">UC</th>
                                <th className="px-4 py-3">Parcela</th>
                                <th className="px-4 py-3">Vencimento</th>
                                <th className="px-4 py-3 text-right">Valor</th>
                                <th className="px-4 py-3">Situação</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map(l => {
                                const acao = ACAO_ETAPA[l.etapa];
                                return (
                                    <tr key={l.parcela_id} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{l.cliente}</td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 tabular-nums">{l.uc}</td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{l.numero_parcela === 0 ? 'Entrada' : `${l.numero_parcela}ª`}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-gray-700 dark:text-gray-200 tabular-nums">{dataBr(l.data_vencimento)}</span>
                                            <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${l.dias_para_vencer < 0
                                                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                                : l.dias_para_vencer <= 3
                                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                                                {l.dias_para_vencer < 0 ? <><AlertTriangle size={11} /> venceu há {-l.dias_para_vencer} dia(s)</>
                                                    : l.dias_para_vencer === 0 ? 'vence hoje' : `em ${l.dias_para_vencer} dia(s)`}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100 tabular-nums">{brl(l.valor)}</td>
                                        <td className="px-4 py-3"><EtapaBoletoBadge etapa={l.etapa} /></td>
                                        <td className="px-4 py-3 text-right">
                                            {acao ? (
                                                <button
                                                    type="button"
                                                    onClick={() => abrir(l.fatura_id)}
                                                    disabled={abrindo === l.fatura_id}
                                                    className={acao === 'abrir' ? botaoSecundario : botaoPrimario}
                                                >
                                                    {abrindo === l.fatura_id ? <Loader2 size={13} className="animate-spin" />
                                                        : acao === 'enviar' ? <Send size={13} /> : <FileText size={13} />}
                                                    {acao === 'emitir' ? 'Emitir' : acao === 'enviar' ? 'Enviar' : 'Abrir'}
                                                </button>
                                            ) : (
                                                <button type="button" onClick={() => abrirEmFaturas(l)} className={botaoSecundario}
                                                    title="Esta fatura está em outro passo: abra-a na tela de Faturas">
                                                    <ExternalLink size={13} /> Em Faturas
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {telas}
        </div>
    );
};

export default ParcelasParaEmitir;
